import type {
  CustomerAttributeSourceType,
  CustomerAttributeType,
  CustomerFieldSuggestions,
  TemplateAttributeAutocompleteSource,
  TemplateAttributeControl,
  TemplateAttributeOption,
} from '@protoss/shared';
import { formatPhoneInput, normalizeFieldKey, normalizePhoneForStorage } from '@protoss/shared';
import {
  AutocompleteField,
  Button,
  IconSize,
  MultiSelect,
  Select,
  SelectItem,
  SkeletonBlock,
  SkeletonCircle,
  SkeletonProvider,
  SkeletonText,
  TagInput,
  TextArea,
  TextField,
  TrashIcon,
} from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog';
import { useDirtyState } from '../../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { isLikelyHttpUrl, normalizeLinkedinInput } from '../../../lib/url';
import type {
  CustomerDetailRecord,
  CustomerFormPayload,
  CustomerTemplateRecord,
} from '../services/customersApi';
import { customersApi } from '../services/customersApi';
import { CustomerAvatar } from './CustomerAvatar';
import type { NewAttributeDraft } from './CustomerRightRailPanels';

export interface CustomerImageSubmission {
  imageFile: File | null;
  removeImage: boolean;
}

interface CustomerFormProps {
  projectUuid?: string;
  initial?: Partial<CustomerDetailRecord>;
  onSubmit: (data: CustomerFormPayload, image: CustomerImageSubmission) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  /** HTML id for the <form> element - lets external submit buttons in the shell header use the form attribute. */
  formId?: string;
  /** When true the bottom action buttons are not rendered - they live in the shell header instead. */
  hideActions?: boolean;
  /** Called whenever submitting or canSubmit changes so the parent can drive header buttons. */
  onFormStateChange?: (state: { submitting: boolean; canSubmit: boolean }) => void;
  /**
   * Bumped by the parent after a successful save to force the dirty-state snapshot to
   * re-run against the reloaded `initial` values, resetting isDirty to false.
   */
  snapshotKey?: number;
  /** Completed attribute draft from AttributeDesignerPanel; consumed once then cleared. */
  pendingAttributeDraft?: NewAttributeDraft | null;
  /** Called after pendingAttributeDraft has been consumed and appended. */
  onPendingAttributeConsumed?: () => void;
  /** Signal emitted when an attribute is deleted from the right-rail editor. */
  deletedAttributeSignal?: { fieldKey: string; name: string; nonce: number } | null;
  /**
   * Called after a deleted custom attribute's stored value has been permanently removed.
   * The parent should trigger a data reload to reflect the change.
   */
  onAttributeValueDeleted?: () => Promise<void>;
}

interface EditableAttribute {
  id: string;
  fieldKey: string;
  name: string;
  type: CustomerAttributeType;
  control: TemplateAttributeControl;
  placeholder: string;
  autocompleteSource: TemplateAttributeAutocompleteSource | null;
  sectionKey: string;
  aiEnabled: boolean;
  description: string;
  aiPrompt: string;
  rawValue: string;
  boolValue: boolean;
  multiValues: string[];
  optionsText: string;
  sourceType: string;
  sourceRefId: string;
  confidenceScore: number | null;
  displayOrder: number;
  saveToUserTemplate: boolean;
  fromTemplate: boolean;
  scope: 'workspace' | 'user' | 'customer';
  deleted?: boolean;
}

interface CustomerDirtyAttributePayload {
  key: string;
  value: boolean | number | string | string[] | null;
}

interface CustomerDirtyPayload {
  name: string;
  email: string;
  phone: string;
  image:
    | { kind: 'none' }
    | { kind: 'existing'; assetUuid: string }
    | { kind: 'remove' }
    | {
        kind: 'upload';
        fileName: string;
        fileSize: number;
        lastModified: number;
        fileType: string;
      };
  attributes: CustomerDirtyAttributePayload[];
}

const IMAGE_ACCEPT_ATTR = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

function buildClientId() {
  return `attribute-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toOptionsText(options?: TemplateAttributeOption[] | null): string {
  return (options ?? []).map((option) => option.label).join(', ');
}

function normalizeSourceType(value: string): CustomerAttributeSourceType | null {
  const normalized = value.trim();
  switch (normalized) {
    case 'manual':
    case 'meeting_transcript':
    case 'workspace_note':
    case 'integration':
    case 'ai_inference':
      return normalized;
    default:
      return null;
  }
}

function parseOptions(optionsText: string): TemplateAttributeOption[] | null {
  const values = Array.from(
    new Set(
      optionsText
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );

  if (values.length === 0) return null;

  return values.map((value) => ({
    label: value,
    value,
  }));
}

function isSupportedImageFile(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type);
}

function hasAttributeValue(attribute: EditableAttribute): boolean {
  switch (attribute.type) {
    case 'boolean':
      return attribute.boolValue;
    case 'multi_select':
    case 'tags':
      return attribute.multiValues.length > 0;
    default:
      return attribute.rawValue.trim().length > 0;
  }
}

function isTagsAttribute(attribute: Pick<EditableAttribute, 'fieldKey' | 'control'>): boolean {
  return attribute.control === 'tags' || normalizeFieldKey(attribute.fieldKey) === 'tags';
}

function normalizeCurrentAttributeValue(
  attribute: EditableAttribute
): boolean | number | string | string[] | null {
  if (attribute.type === 'boolean') {
    return attribute.boolValue ? true : null;
  }

  if (attribute.type === 'multi_select' || attribute.type === 'tags') {
    const values = attribute.multiValues
      .map((item) => item.trim())
      .filter(Boolean)
      .sort();
    return values.length > 0 ? values : null;
  }

  if (attribute.type === 'number') {
    const normalized = attribute.rawValue.trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : normalized;
  }

  const normalized = attribute.rawValue.trim();
  if (!normalized) return null;
  if (attribute.control === 'url' || normalizeFieldKey(attribute.fieldKey) === 'linkedin') {
    return normalizeLinkedinInput(normalized) || null;
  }
  return normalized;
}

function normalizePersistedAttributeValue(
  value: unknown,
  fieldType?: string,
  fieldKey?: string
): boolean | number | string | string[] | null {
  if (typeof value === 'boolean') {
    return value ? true : null;
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .sort();
    return normalized.length > 0 ? normalized : null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) return null;

    if (fieldType === 'tags' || fieldType === 'multi_select') {
      const items = normalized
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .sort();
      return items.length > 0 ? items : null;
    }

    if (normalizeFieldKey(fieldKey ?? '') === 'linkedin') {
      return normalizeLinkedinInput(normalized) || null;
    }

    return normalized;
  }

  return null;
}

function shouldTrackCustomerAttribute(attribute: EditableAttribute): boolean {
  if (attribute.deleted) return false;
  if (attribute.sectionKey === 'notes') return false;
  return attribute.sectionKey === 'identity' || attribute.scope === 'customer';
}

function shouldTrackInitialAttribute(
  attribute: NonNullable<CustomerDetailRecord['attributes']>[number],
  templateFieldKeys: Set<string>
): boolean {
  if (attribute.deleted) return false;
  if (attribute.sectionKey === 'notes') return false;
  return (
    attribute.sectionKey === 'identity' ||
    !templateFieldKeys.has(normalizeFieldKey(attribute.fieldKey))
  );
}

function buildCustomerDirtyPayload(args: {
  initialImageAssetUuid?: string | null;
  name: string;
  email: string;
  phone: string;
  selectedImageFile: File | null;
  removeImage: boolean;
  attributes: EditableAttribute[];
}): CustomerDirtyPayload {
  const { attributes, email, initialImageAssetUuid, name, phone, removeImage, selectedImageFile } =
    args;

  const image = removeImage
    ? ({ kind: 'remove' } as const)
    : selectedImageFile
      ? ({
          kind: 'upload',
          fileName: selectedImageFile.name,
          fileSize: selectedImageFile.size,
          lastModified: selectedImageFile.lastModified,
          fileType: selectedImageFile.type,
        } as const)
      : initialImageAssetUuid
        ? ({ kind: 'existing', assetUuid: initialImageAssetUuid } as const)
        : ({ kind: 'none' } as const);

  const normalizedAttributes = attributes
    .filter(shouldTrackCustomerAttribute)
    .map((attribute) => ({
      key: normalizeFieldKey(attribute.fieldKey || attribute.name),
      value: normalizeCurrentAttributeValue(attribute),
    }))
    .filter((attribute) => attribute.key && attribute.value !== null)
    .sort((left, right) => left.key.localeCompare(right.key));

  return {
    name: name.trim(),
    email: email.trim(),
    phone: normalizePhoneForStorage(phone) ?? '',
    image,
    attributes: normalizedAttributes,
  };
}

function buildCustomerBaselinePayload(
  initial?: Partial<CustomerDetailRecord>
): CustomerDirtyPayload {
  const templateFieldKeys = new Set(
    (initial?.template?.mergedAttributes ?? []).map((attribute) =>
      normalizeFieldKey(attribute.fieldKey)
    )
  );

  const normalizedAttributes = (initial?.attributes ?? [])
    .filter((attribute) => shouldTrackInitialAttribute(attribute, templateFieldKeys))
    .map((attribute) => ({
      key: normalizeFieldKey(attribute.fieldKey || attribute.name),
      value: normalizePersistedAttributeValue(
        attribute.value,
        attribute.fieldType,
        attribute.fieldKey
      ),
    }))
    .filter((attribute) => attribute.key && attribute.value !== null)
    .sort((left, right) => left.key.localeCompare(right.key));

  return {
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: normalizePhoneForStorage(formatPhoneInput(initial?.phone)) ?? '',
    image: initial?.primaryImageAsset?.uuid
      ? { kind: 'existing', assetUuid: initial.primaryImageAsset.uuid }
      : { kind: 'none' },
    attributes: normalizedAttributes,
  };
}

function attributeValueToDraft(
  value: unknown,
  fieldType?: string
): Pick<EditableAttribute, 'rawValue' | 'boolValue' | 'multiValues'> {
  if (Array.isArray(value)) {
    return {
      rawValue: '',
      boolValue: false,
      multiValues: value.map((item) => String(item)),
    };
  }

  if (typeof value === 'boolean') {
    return {
      rawValue: '',
      boolValue: value,
      multiValues: [],
    };
  }

  if (
    (fieldType === 'tags' || fieldType === 'multi_select') &&
    typeof value === 'string' &&
    value.trim()
  ) {
    return {
      rawValue: '',
      boolValue: false,
      multiValues: value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  if (typeof value === 'number') {
    return {
      rawValue: String(value),
      boolValue: false,
      multiValues: [],
    };
  }

  return {
    rawValue: typeof value === 'string' ? value : '',
    boolValue: false,
    multiValues: [],
  };
}

function buildEditableAttributes(
  template: CustomerTemplateRecord | null,
  initialAttributes: CustomerDetailRecord['attributes'] | undefined
): EditableAttribute[] {
  const attributeMap = new Map(
    (initialAttributes ?? []).map((attribute) => [normalizeFieldKey(attribute.fieldKey), attribute])
  );

  // Build a set of field keys that originate from workspace defaults.
  // syncWorkspaceTemplateToUserTemplate mirrors all workspace rows into user-scope rows,
  // so mergedAttributes has scope='user' for everything - making the stored scope value
  // an unreliable signal for "user explicitly added this custom field".
  // Instead, use workspace origin: if the field_key is in workspaceAttributes it is a
  // workspace default (scope:'workspace' for form visibility purposes); only field keys
  // that are exclusively in userAttributes are truly user-added custom fields (scope:'user').
  const workspaceFieldKeySet = new Set(
    (template?.workspaceAttributes ?? []).map((a) => normalizeFieldKey(a.fieldKey))
  );

  const merged = [...(template?.mergedAttributes ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
    .map((attribute, index) => {
      const current = attributeMap.get(normalizeFieldKey(attribute.fieldKey));
      const resolvedFieldType = current?.fieldType ?? attribute.fieldType;
      const valueState = attributeValueToDraft(current?.value, resolvedFieldType);
      const derivedScope = workspaceFieldKeySet.has(normalizeFieldKey(attribute.fieldKey))
        ? ('workspace' as const)
        : ('user' as const);

      return {
        id: current?.uuid ?? `${attribute.fieldKey}-${index}`,
        fieldKey: attribute.fieldKey,
        name: current?.name ?? attribute.name,
        type: current?.fieldType ?? attribute.fieldType,
        control: attribute.control,
        placeholder: attribute.placeholder ?? '',
        autocompleteSource: attribute.autocompleteSource ?? null,
        sectionKey: current?.sectionKey ?? attribute.sectionKey,
        aiEnabled: current?.aiEnabled ?? attribute.aiEnabled,
        description: current?.description ?? attribute.description ?? '',
        aiPrompt: attribute.aiPrompt ?? '',
        rawValue: valueState.rawValue,
        boolValue: valueState.boolValue,
        multiValues: valueState.multiValues,
        optionsText: toOptionsText(attribute.options),
        sourceType: current?.sourceType ?? '',
        sourceRefId: current?.sourceRefId ?? '',
        confidenceScore: current?.confidenceScore ?? null,
        displayOrder: attribute.displayOrder,
        saveToUserTemplate: derivedScope === 'user',
        fromTemplate: true,
        scope: derivedScope,
      } satisfies EditableAttribute;
    });

  const extras = (initialAttributes ?? [])
    .filter(
      (attribute) =>
        !(template?.mergedAttributes ?? []).some(
          (templateAttribute) =>
            normalizeFieldKey(templateAttribute.fieldKey) === normalizeFieldKey(attribute.fieldKey)
        )
    )
    .map((attribute) => {
      const valueState = attributeValueToDraft(attribute.value, attribute.fieldType);
      return {
        id: attribute.uuid ?? buildClientId(),
        fieldKey: normalizeFieldKey(attribute.fieldKey),
        name: attribute.name,
        type: attribute.fieldType,
        control: 'text',
        placeholder: '',
        autocompleteSource: null,
        sectionKey: attribute.sectionKey,
        aiEnabled: attribute.aiEnabled,
        description: attribute.description ?? '',
        aiPrompt: '',
        rawValue: valueState.rawValue,
        boolValue: valueState.boolValue,
        multiValues: valueState.multiValues,
        optionsText: '',
        sourceType: attribute.sourceType ?? '',
        sourceRefId: attribute.sourceRefId ?? '',
        confidenceScore: attribute.confidenceScore ?? null,
        displayOrder: attribute.displayOrder,
        saveToUserTemplate: false,
        fromTemplate: false,
        scope: 'customer' as const,
        deleted: attribute.deleted ?? false,
      };
    });

  return [...merged, ...extras];
}

function buildLoadingAttributes(): EditableAttribute[] {
  return [
    {
      id: 'loading-attribute-company',
      fieldKey: 'loading_company',
      name: 'Company',
      type: 'text',
      control: 'autocomplete',
      placeholder: '',
      autocompleteSource: null,
      sectionKey: 'identity',
      aiEnabled: false,
      description: '',
      aiPrompt: '',
      rawValue: '',
      boolValue: false,
      multiValues: [],
      optionsText: '',
      sourceType: '',
      sourceRefId: '',
      confidenceScore: null,
      displayOrder: 10,
      saveToUserTemplate: false,
      fromTemplate: true,
      scope: 'workspace',
    },
    {
      id: 'loading-attribute-role',
      fieldKey: 'loading_role',
      name: 'Role',
      type: 'text',
      control: 'select',
      placeholder: '',
      autocompleteSource: null,
      sectionKey: 'identity',
      aiEnabled: false,
      description: '',
      aiPrompt: '',
      rawValue: '',
      boolValue: false,
      multiValues: [],
      optionsText: '',
      sourceType: '',
      sourceRefId: '',
      confidenceScore: null,
      displayOrder: 20,
      saveToUserTemplate: false,
      fromTemplate: true,
      scope: 'workspace',
    },
    {
      id: 'loading-attribute-notes',
      fieldKey: 'loading_notes',
      name: 'Notes',
      type: 'long_text',
      control: 'textarea',
      placeholder: '',
      autocompleteSource: null,
      sectionKey: 'identity',
      aiEnabled: false,
      description: '',
      aiPrompt: '',
      rawValue: '',
      boolValue: false,
      multiValues: [],
      optionsText: '',
      sourceType: '',
      sourceRefId: '',
      confidenceScore: null,
      displayOrder: 30,
      saveToUserTemplate: false,
      fromTemplate: true,
      scope: 'workspace',
    },
    {
      id: 'loading-attribute-tags',
      fieldKey: 'loading_tags',
      name: 'Tags',
      type: 'tags',
      control: 'tags',
      placeholder: '',
      autocompleteSource: null,
      sectionKey: 'identity',
      aiEnabled: false,
      description: '',
      aiPrompt: '',
      rawValue: '',
      boolValue: false,
      multiValues: [],
      optionsText: '',
      sourceType: '',
      sourceRefId: '',
      confidenceScore: null,
      displayOrder: 40,
      saveToUserTemplate: false,
      fromTemplate: true,
      scope: 'workspace',
    },
  ];
}

function renderCustomerAttributeControlSkeleton(
  control: TemplateAttributeControl,
  attributeType: CustomerAttributeType
) {
  if (control === 'boolean' || attributeType === 'boolean') {
    return (
      <div className="customer-form__attr-bool-skeleton" aria-hidden="true">
        <SkeletonBlock className="customer-form__checkbox-skeleton" />
        <SkeletonBlock className="customer-form__checkbox-label-skeleton" />
      </div>
    );
  }

  if (control === 'textarea') {
    return (
      <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--textarea" />
    );
  }

  if (control === 'tags' || control === 'multi_select' || attributeType === 'multi_select') {
    return (
      <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--tags" />
    );
  }

  if (control === 'select' || control === 'autocomplete') {
    return (
      <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--select" />
    );
  }

  return (
    <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--comfortable" />
  );
}

/**
 * CustomerForm - dynamic customer-first editor with template layering and AI-ready attributes.
 */
export function CustomerForm({
  projectUuid,
  initial,
  onSubmit,
  onCancel,
  submitLabel = 'Save customer',
  formId,
  hideActions,
  onFormStateChange,
  snapshotKey,
  pendingAttributeDraft,
  onPendingAttributeConsumed,
  deletedAttributeSignal,
  onAttributeValueDeleted,
}: CustomerFormProps) {
  const initialCustomerKey = initial?.uuid ?? '__new_customer__';
  const initialImageKey = initial?.primaryImageAsset?.uuid ?? '__no_image__';
  const [name, setName] = React.useState(initial?.name ?? '');
  const [email, setEmail] = React.useState(initial?.email ?? '');
  const [phone, setPhone] = React.useState(formatPhoneInput(initial?.phone));
  const [template, setTemplate] = React.useState<CustomerTemplateRecord | null>(
    initial?.template ?? null
  );
  const [attributes, setAttributes] = React.useState<EditableAttribute[]>(
    buildEditableAttributes(initial?.template ?? null, initial?.attributes)
  );
  // Track the most-recently-added attribute id (for future scroll-to-focus)
  const [, setFocusedAttributeId] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  // Start in loading state when no pre-loaded template is provided.
  // This prevents a flash of the interactive form before the effect fires.
  const [loadingTemplate, setLoadingTemplate] = React.useState<boolean>(() => !initial?.template);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(null);
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = React.useState<string | null>(null);
  const [removeImage, setRemoveImage] = React.useState(false);

  // Validation state
  const [emailTouched, setEmailTouched] = React.useState(false);
  const [phoneTouched, setPhoneTouched] = React.useState(false);
  const [duplicateCustomer, setDuplicateCustomer] = React.useState<{
    customerUuid: string;
    customerName: string | null;
  } | null>(null);
  const [emailChecking, setEmailChecking] = React.useState(false);
  const [fieldSuggestions, setFieldSuggestions] = React.useState<CustomerFieldSuggestions>({
    companies: [],
    titles: [],
    industries: [],
    locations: [],
  });

  // Attribute value deletion dialog state
  const [deletingAttributeValue, setDeletingAttributeValue] = React.useState<{
    fieldKey: string;
    name: string;
    currentValue: string;
  } | null>(null);
  const [deletingAttributeValuePending, setDeletingAttributeValuePending] = React.useState(false);

  const emailFormatError = React.useMemo(() => {
    if (!emailTouched || !email.trim()) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? null : 'Enter a valid email address';
  }, [email, emailTouched]);

  const emailDuplicateError = duplicateCustomer
    ? 'Duplicate email found. A customer with this email already exists.'
    : null;

  const emailError = emailFormatError ?? emailDuplicateError;

  const isEditMode = Boolean(initial?.uuid);

  const {
    isDirty,
    setBaseline: setDirtyBaseline,
    updateCurrent: updateDirtyCurrent,
  } = useDirtyState<CustomerDirtyPayload>({ debounceMs: 120 });

  const baselinePayload = React.useMemo(
    () => buildCustomerBaselinePayload(initial),
    [initial, initialCustomerKey, initialImageKey, snapshotKey]
  );

  const currentDirtyPayload = React.useMemo(
    () =>
      buildCustomerDirtyPayload({
        initialImageAssetUuid: initial?.primaryImageAsset?.uuid ?? null,
        name,
        email,
        phone,
        selectedImageFile,
        removeImage,
        attributes,
      }),
    [
      attributes,
      email,
      initial?.primaryImageAsset?.uuid,
      name,
      phone,
      removeImage,
      selectedImageFile,
    ]
  );

  React.useEffect(() => {
    setDirtyBaseline(baselinePayload);
  }, [baselinePayload, setDirtyBaseline]);

  React.useEffect(() => {
    updateDirtyCurrent(currentDirtyPayload);
  }, [currentDirtyPayload, updateDirtyCurrent]);

  const canSubmit = !submitting && !!name.trim() && !emailError && !emailChecking && isDirty;

  useUnsavedChanges(
    !submitting && isDirty,
    'customer-form',
    isEditMode ? 'Update customer' : 'Create customer'
  );

  React.useEffect(() => {
    onFormStateChange?.({ submitting, canSubmit });
  }, [submitting, canSubmit, onFormStateChange]);

  const phoneError = React.useMemo(() => {
    if (!phoneTouched || !phone.trim()) return null;
    return /^\(\d{3}\)\s\d{3}-\d{4}$/.test(phone.trim()) ? null : 'Use format (555) 123-1234';
  }, [phone, phoneTouched]);

  React.useEffect(() => {
    setName(initial?.name ?? '');
    setEmail(initial?.email ?? '');
    setPhone(formatPhoneInput(initial?.phone));
    setSelectedImageFile(null);
    setSelectedImagePreviewUrl(null);
    setRemoveImage(false);
  }, [initialCustomerKey, initialImageKey, snapshotKey]);

  React.useEffect(() => {
    if (!selectedImageFile) {
      setSelectedImagePreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedImageFile);
    setSelectedImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedImageFile]);

  React.useEffect(() => {
    if (initial?.template) {
      setTemplate(initial.template);
      setLoadingTemplate(false);
      return;
    }

    let cancelled = false;
    setLoadingTemplate(true);
    customersApi
      .getTemplate(projectUuid)
      .then(({ template: loadedTemplate }) => {
        if (!cancelled) {
          setTemplate(loadedTemplate);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError((err as Error).message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplate(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectUuid, initial?.template]);

  React.useEffect(() => {
    let cancelled = false;

    customersApi
      .getFieldSuggestions(projectUuid)
      .then(({ suggestions }) => {
        if (!cancelled) {
          setFieldSuggestions(suggestions);
        }
      })
      .catch(() => {
        // Best effort only.
      });

    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  // Debounced email duplicate check - fires when the address has a valid format.
  // Skip entirely when editing and the address hasn't changed from the saved value.
  React.useEffect(() => {
    const isValidFormat = email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidFormat) {
      setDuplicateCustomer(null);
      setEmailChecking(false);
      return;
    }
    // In edit mode, the customer's own email can never be a duplicate - skip the check.
    if (
      initial?.uuid &&
      initial.email &&
      email.trim().toLowerCase() === initial.email.trim().toLowerCase()
    ) {
      setDuplicateCustomer(null);
      setEmailChecking(false);
      return;
    }
    setEmailChecking(true);
    setDuplicateCustomer(null);
    const timer = setTimeout(() => {
      let cancelled = false;
      customersApi
        .checkEmailExists(email.trim(), projectUuid, initial?.uuid)
        .then(({ exists, customerUuid, customerName }) => {
          if (!cancelled) {
            setDuplicateCustomer(
              exists && customerUuid
                ? {
                    customerUuid,
                    customerName,
                  }
                : null
            );
            setEmailChecking(false);
          }
        })
        .catch(() => {
          if (!cancelled) setEmailChecking(false);
          // Best-effort - don't block submission on network failure
        });
      return () => {
        cancelled = true;
      };
    }, 400);
    return () => {
      clearTimeout(timer);
      setEmailChecking(false);
    };
  }, [email, projectUuid, initial?.uuid]);

  React.useEffect(() => {
    setAttributes(buildEditableAttributes(template, initial?.attributes));
  }, [template, initialCustomerKey]);

  React.useEffect(() => {
    if (!deletedAttributeSignal?.fieldKey) return;
    const deletedKey = normalizeFieldKey(deletedAttributeSignal.fieldKey);
    const deletedNameKey = normalizeFieldKey(deletedAttributeSignal.name);
    setAttributes((current) =>
      current.map((attribute) => {
        const attributeFieldKey = normalizeFieldKey(attribute.fieldKey);
        const attributeNameKey = normalizeFieldKey(attribute.name);
        if (attributeFieldKey === deletedKey || attributeNameKey === deletedNameKey) {
          return { ...attribute, deleted: true };
        }
        return attribute;
      })
    );
  }, [
    deletedAttributeSignal?.nonce,
    deletedAttributeSignal?.fieldKey,
    deletedAttributeSignal?.name,
  ]);

  const visibleAttributes = React.useMemo(() => {
    // Show identity-section template attributes + any customer-scope attributes
    // (newly added drafts via the right-rail designer, or stored values without a
    // template entry). Newly added drafts may belong to any section, so they must
    // always appear in the form regardless of their sectionKey.
    // Deleted customer-scope attributes: show only when they have a stored value.
    // Notes attributes (sectionKey === 'notes') are handled by their own tab, not here.
    const basicAttributes = attributes.filter((attribute) => {
      if (attribute.sectionKey === 'notes') return false;
      if (attribute.sectionKey !== 'identity' && attribute.scope !== 'customer') return false;
      if (attribute.deleted) return hasAttributeValue(attribute);
      return true;
    });
    const nonTagAttributes = basicAttributes
      .filter((attribute) => !isTagsAttribute(attribute))
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    const tagAttributes = basicAttributes
      .filter((attribute) => isTagsAttribute(attribute))
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    return [...nonTagAttributes, ...tagAttributes];
  }, [attributes]);

  const loadingAttributes = React.useMemo(() => buildLoadingAttributes(), []);
  const renderedAttributes =
    loadingTemplate && visibleAttributes.length === 0 ? loadingAttributes : visibleAttributes;

  const hasExistingImage = Boolean(initial?.primaryImageAsset && !removeImage);
  const hasPendingImage = Boolean(selectedImageFile);
  const canToggleImageState = Boolean(
    initial?.primaryImageAsset || selectedImageFile || removeImage
  );

  const updateAttribute = React.useCallback(
    (attributeId: string, updater: (attribute: EditableAttribute) => EditableAttribute) => {
      setAttributes((current) =>
        current.map((attribute) => (attribute.id === attributeId ? updater(attribute) : attribute))
      );
    },
    []
  );

  // Consume pendingAttributeDraft from the right-rail designer
  React.useEffect(() => {
    if (!pendingAttributeDraft) return;
    const id = buildClientId();
    setAttributes((current) => {
      // Only look at identity-section non-tags attributes as the baseline
      // so the new field's display order slots in right after the identity block.
      const maxNonTagsOrder = current.reduce(
        (max, attr) =>
          attr.sectionKey === 'identity' && attr.displayOrder < 1000
            ? Math.max(max, attr.displayOrder)
            : max,
        0
      );
      const next: EditableAttribute = {
        id,
        fieldKey: '',
        name: pendingAttributeDraft.name,
        type: pendingAttributeDraft.fieldType,
        control: pendingAttributeDraft.fieldType as TemplateAttributeControl,
        placeholder: '',
        autocompleteSource: null,
        sectionKey: pendingAttributeDraft.sectionKey,
        aiEnabled: pendingAttributeDraft.aiEnabled,
        description: pendingAttributeDraft.description,
        aiPrompt: pendingAttributeDraft.aiPrompt,
        rawValue: '',
        boolValue: false,
        multiValues: [],
        optionsText: '',
        sourceType: '',
        sourceRefId: '',
        confidenceScore: null,
        displayOrder: maxNonTagsOrder + 10,
        saveToUserTemplate: true,
        fromTemplate: false,
        scope: 'customer',
      };
      return [...current, next];
    });
    setFocusedAttributeId(id);
    onPendingAttributeConsumed?.();
  }, [pendingAttributeDraft, onPendingAttributeConsumed]);

  const handleImageSelected = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    if (!isSupportedImageFile(file)) {
      setError('Use a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError('Customer images must be 5 MB or smaller.');
      return;
    }

    setError(null);
    setSelectedImageFile(file);
    setRemoveImage(false);
  }, []);

  const handleDeleteAttributeValue = React.useCallback(async () => {
    if (!deletingAttributeValue || !initial?.uuid) return;
    setDeletingAttributeValuePending(true);
    try {
      await customersApi.deleteCustomerAttributeValue(
        projectUuid,
        initial.uuid,
        deletingAttributeValue.fieldKey
      );
      setDeletingAttributeValue(null);
      await onAttributeValueDeleted?.();
    } finally {
      setDeletingAttributeValuePending(false);
    }
  }, [deletingAttributeValue, initial?.uuid, projectUuid, onAttributeValueDeleted]);

  const clearImageSelection = React.useCallback(() => {
    setSelectedImageFile(null);
    setSelectedImagePreviewUrl(null);
    setRemoveImage((current) => (current ? false : Boolean(initial?.primaryImageAsset)));
  }, [initial?.primaryImageAsset]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    setError(null);

    const payloadAttributes = attributes
      .filter((attribute) => attribute.name.trim())
      .filter((attribute) => {
        if (attribute.scope === 'user') return true;
        if (attribute.fromTemplate && !hasAttributeValue(attribute)) return false;
        return true;
      })
      .map((attribute) => {
        const normalizedFieldKey = normalizeFieldKey(attribute.fieldKey || attribute.name);
        const options = parseOptions(attribute.optionsText);

        let value: NonNullable<CustomerFormPayload['attributes']>[number]['value'];
        if (attribute.type === 'boolean') {
          value = attribute.boolValue;
        } else if (attribute.type === 'multi_select' || attribute.type === 'tags') {
          value = attribute.multiValues;
        } else if (attribute.type === 'number') {
          value = attribute.rawValue.trim() ? Number(attribute.rawValue) : null;
        } else {
          value = attribute.rawValue.trim() || null;
        }

        const normalizedRaw = attribute.rawValue.trim();
        const normalizedLinkedin =
          attribute.control === 'url' || normalizeFieldKey(attribute.fieldKey) === 'linkedin'
            ? normalizeLinkedinInput(normalizedRaw)
            : normalizedRaw;

        return {
          fieldKey: normalizedFieldKey || undefined,
          name: attribute.name.trim(),
          fieldType: attribute.type,
          control: attribute.control,
          placeholder: attribute.placeholder || null,
          autocompleteSource: attribute.autocompleteSource,
          sectionKey: attribute.sectionKey,
          aiEnabled: attribute.aiEnabled,
          description: attribute.description.trim() || null,
          aiPrompt: attribute.aiPrompt.trim() || null,
          options,
          value: typeof value === 'string' ? normalizedLinkedin || null : value,
          sourceType: normalizeSourceType(attribute.sourceType),
          sourceRefId: attribute.sourceRefId.trim() || null,
          confidenceScore: attribute.confidenceScore,
          saveToUserTemplate: attribute.scope === 'user' ? true : attribute.saveToUserTemplate,
        };
      });

    try {
      await onSubmit(
        {
          name: name.trim(),
          email: email.trim() || null,
          phone: normalizePhoneForStorage(phone),
          attributes: payloadAttributes,
        },
        {
          imageFile: selectedImageFile,
          removeImage,
        }
      );
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SkeletonProvider loading={loadingTemplate}>
        <form className="customer-form" id={formId} onSubmit={handleSubmit} noValidate>
          {/* Hidden file input - triggers image selection */}
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT_ATTR}
            className="customer-form__file-input"
            onChange={handleImageSelected}
          />

          {/* ── Profile: Avatar + Name ─────────────────────────────────────── */}
          <div className="customer-form__profile">
            {/* Avatar upload zone */}
            <div
              className="customer-form__avatar-zone"
              role={loadingTemplate ? undefined : 'button'}
              tabIndex={submitting || loadingTemplate ? -1 : 0}
              aria-label={
                hasExistingImage || hasPendingImage
                  ? 'Replace customer image'
                  : 'Upload customer image'
              }
              onClick={() => !submitting && !loadingTemplate && fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (
                  !submitting &&
                  !loadingTemplate &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              {loadingTemplate ? (
                <SkeletonCircle className="customer-form__avatar-hero-skeleton" size="88px" />
              ) : (
                <>
                  <CustomerAvatar
                    name={name || initial?.name || 'Customer'}
                    asset={removeImage ? null : (initial?.primaryImageAsset ?? null)}
                    srcOverride={selectedImagePreviewUrl}
                    size="lg"
                    className="customer-form__avatar-hero"
                  />
                  <div className="customer-form__avatar-overlay" aria-hidden="true">
                    <span className="customer-form__avatar-overlay-text">
                      {hasExistingImage || hasPendingImage ? 'Replace' : 'Upload'}
                    </span>
                  </div>
                </>
              )}
            </div>

            {loadingTemplate ? (
              <div
                className="customer-form__name-field customer-form__loading-field"
                aria-hidden="true"
              >
                <SkeletonText className="customer-form__loading-label" width="32%">
                  Name
                </SkeletonText>
                <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--comfortable" />
              </div>
            ) : (
              <TextField
                label="Name *"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enterprise Emily"
                disabled={submitting}
                required
                size="comfortable"
                className="customer-form__name-field"
              />
            )}
          </div>

          {/* Image remove action - only shown when an image exists */}
          {canToggleImageState && !loadingTemplate && (
            <div className="customer-form__image-actions">
              <button
                type="button"
                className="customer-form__image-remove-btn"
                onClick={clearImageSelection}
                disabled={submitting}
              >
                {removeImage ? 'Undo remove' : 'Remove'}
              </button>
            </div>
          )}

          {/* Email + Phone */}
          <div className="customer-form__row">
            {loadingTemplate ? (
              <>
                <div className="customer-form__loading-field" aria-hidden="true">
                  <SkeletonText className="customer-form__loading-label" width="28%">
                    Email
                  </SkeletonText>
                  <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--comfortable" />
                </div>
                <div className="customer-form__loading-field" aria-hidden="true">
                  <SkeletonText className="customer-form__loading-label" width="28%">
                    Phone
                  </SkeletonText>
                  <SkeletonBlock className="customer-form__loading-input customer-form__loading-input--comfortable" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="name@company.com"
                    disabled={submitting}
                    size="comfortable"
                    error={emailError ?? undefined}
                  />
                  {duplicateCustomer && (
                    <p className="customer-form__error" role="alert">
                      Existing customer:{' '}
                      <a
                        className="customer-form__duplicate-link"
                        href={
                          projectUuid
                            ? `/project/${projectUuid}/customers/${duplicateCustomer.customerUuid}`
                            : `/po/customers/${duplicateCustomer.customerUuid}`
                        }
                      >
                        {duplicateCustomer.customerName?.trim() || 'Open existing customer'}
                      </a>
                    </p>
                  )}
                  {emailChecking && !emailError && (
                    <p className="customer-form__email-checking">
                      <span className="customer-form__email-checking-spinner" aria-hidden="true" />
                      Validating email and checking for duplicates…
                    </p>
                  )}
                </div>
                <TextField
                  label="Phone"
                  value={phone}
                  onChange={(event) => setPhone(formatPhoneInput(event.target.value))}
                  onBlur={() => setPhoneTouched(true)}
                  placeholder="(555) 123-4567"
                  disabled={submitting}
                  size="comfortable"
                  error={phoneError ?? undefined}
                />
              </>
            )}
          </div>

          {renderedAttributes.length > 0 && (
            <div className="customer-form__attr-rows">
              {renderedAttributes.map((attribute) => {
                const selectOptions = parseOptions(attribute.optionsText) ?? [];
                const control = (attribute.control || attribute.type) as TemplateAttributeControl;
                const autocompleteOptions =
                  attribute.autocompleteSource === 'companies'
                    ? fieldSuggestions.companies
                    : attribute.autocompleteSource === 'industries'
                      ? fieldSuggestions.industries
                      : attribute.autocompleteSource === 'locations'
                        ? fieldSuggestions.locations
                        : attribute.autocompleteSource === 'titles'
                          ? fieldSuggestions.titles
                          : [];
                const tagsPlaceholder = attribute.placeholder || 'vip, champion, q2-target';
                const placeholder = attribute.placeholder || 'Value';

                return (
                  <div
                    key={attribute.id}
                    className={[
                      'customer-form__attr-row',
                      isTagsAttribute(attribute) ? 'customer-form__attr-row--tags' : '',
                      attribute.deleted ? 'customer-form__attr-row--deleted' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {/* Label */}
                    {attribute.deleted && isEditMode && !loadingTemplate ? (
                      <div className="customer-form__attr-label-row">
                        <span className="customer-form__field-label">
                          {attribute.name || 'New attribute'}
                          <span className="attr-desc-item__custom-badge customer-form__attr-deleted-badge">
                            {attribute.scope === 'user' || attribute.scope === 'customer'
                              ? ' (custom, deleted)'
                              : ' (deleted)'}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="customer-form__attr-delete-btn"
                          aria-label={`Delete stored value for ${attribute.name}`}
                          onClick={() => {
                            let currentValue = attribute.rawValue;
                            if (attribute.type === 'boolean') {
                              currentValue = String(attribute.boolValue);
                            } else if (
                              attribute.type === 'multi_select' ||
                              attribute.type === 'tags'
                            ) {
                              currentValue = attribute.multiValues.join(', ');
                            }
                            setDeletingAttributeValue({
                              fieldKey: attribute.fieldKey,
                              name: attribute.name,
                              currentValue,
                            });
                          }}
                        >
                          <TrashIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <span className="customer-form__field-label">
                        {loadingTemplate ? (
                          <SkeletonText className="customer-form__attr-label-skeleton" width="44%">
                            {attribute.name || 'New attribute'}
                          </SkeletonText>
                        ) : (
                          <>
                            {attribute.name || 'New attribute'}
                            {(attribute.scope === 'user' || attribute.scope === 'customer') &&
                            attribute.type !== 'tags' ? (
                              <span className="attr-desc-item__custom-badge"> (custom)</span>
                            ) : null}
                          </>
                        )}
                      </span>
                    )}

                    {/* Value control */}
                    <div className="customer-form__attr-value-cell">
                      {(() => {
                        const controlDisabled = submitting || attribute.deleted || loadingTemplate;

                        if (loadingTemplate) {
                          return renderCustomerAttributeControlSkeleton(control, attribute.type);
                        }

                        return (
                          <>
                            {control === 'boolean' || attribute.type === 'boolean' ? (
                              <label className="customer-form__attr-bool-label">
                                <input
                                  type="checkbox"
                                  checked={attribute.boolValue}
                                  onChange={(event) =>
                                    updateAttribute(attribute.id, (current) => ({
                                      ...current,
                                      boolValue: event.target.checked,
                                    }))
                                  }
                                  disabled={controlDisabled}
                                />
                                <span>Yes / true</span>
                              </label>
                            ) : control === 'tags' ? (
                              <TagInput
                                tags={attribute.multiValues}
                                onChange={(tags) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    multiValues: tags,
                                  }))
                                }
                                placeholder={tagsPlaceholder}
                                disabled={controlDisabled}
                                size="comfortable"
                              />
                            ) : control === 'multi_select' || attribute.type === 'multi_select' ? (
                              <MultiSelect
                                options={selectOptions.map((option) => ({
                                  value: option.value,
                                  label: option.label,
                                }))}
                                selectedValues={attribute.multiValues}
                                onSelectionChange={(values) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    multiValues: values,
                                  }))
                                }
                                placeholder={
                                  selectOptions.length > 0 ? placeholder : 'Add options below ↓'
                                }
                                disabled={controlDisabled || selectOptions.length === 0}
                                size="comfortable"
                              />
                            ) : control === 'autocomplete' ? (
                              <AutocompleteField
                                value={attribute.rawValue}
                                onChange={(value) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: value,
                                  }))
                                }
                                options={autocompleteOptions}
                                placeholder={placeholder}
                                disabled={controlDisabled}
                                size="comfortable"
                              />
                            ) : control === 'select' || attribute.type === 'select' ? (
                              selectOptions.length > 0 ? (
                                <Select
                                  value={attribute.rawValue}
                                  onValueChange={(value) =>
                                    updateAttribute(attribute.id, (current) => ({
                                      ...current,
                                      rawValue: value,
                                    }))
                                  }
                                  placeholder={placeholder}
                                  disabled={controlDisabled}
                                  size="comfortable"
                                >
                                  {selectOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </Select>
                              ) : (
                                <TextField
                                  value={attribute.rawValue}
                                  onChange={(event) =>
                                    updateAttribute(attribute.id, (current) => ({
                                      ...current,
                                      rawValue: event.target.value,
                                    }))
                                  }
                                  placeholder="Add options below ↓"
                                  disabled={controlDisabled}
                                  size="comfortable"
                                />
                              )
                            ) : control === 'textarea' ? (
                              <TextArea
                                value={attribute.rawValue}
                                onChange={(event) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: event.target.value,
                                  }))
                                }
                                placeholder={placeholder}
                                disabled={controlDisabled}
                              />
                            ) : control === 'phone' ? (
                              <TextField
                                type="text"
                                value={formatPhoneInput(attribute.rawValue)}
                                onChange={(event) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: formatPhoneInput(event.target.value),
                                  }))
                                }
                                placeholder={attribute.placeholder || '(555) 123-1234'}
                                disabled={controlDisabled}
                                size="comfortable"
                              />
                            ) : control === 'url' ? (
                              <TextField
                                type="url"
                                value={attribute.rawValue}
                                onChange={(event) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: normalizeLinkedinInput(event.target.value),
                                  }))
                                }
                                onBlur={() =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: normalizeLinkedinInput(current.rawValue),
                                  }))
                                }
                                placeholder={
                                  attribute.placeholder || 'https://www.linkedin.com/in/username'
                                }
                                disabled={controlDisabled}
                                size="comfortable"
                                error={
                                  isLikelyHttpUrl(attribute.rawValue)
                                    ? undefined
                                    : 'Enter a valid http(s) URL'
                                }
                              />
                            ) : (
                              <TextField
                                type={
                                  control === 'number' || attribute.type === 'number'
                                    ? 'number'
                                    : 'text'
                                }
                                value={attribute.rawValue}
                                onChange={(event) =>
                                  updateAttribute(attribute.id, (current) => ({
                                    ...current,
                                    rawValue: event.target.value,
                                  }))
                                }
                                placeholder={
                                  control === 'number' || attribute.type === 'number'
                                    ? '42'
                                    : placeholder
                                }
                                disabled={controlDisabled}
                                size="comfortable"
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && !loadingTemplate && <p className="customer-form__error">{error}</p>}

          {!hideActions && (
            <div className="customer-form__actions">
              {loadingTemplate ? (
                <>
                  <SkeletonBlock className="customer-form__action-skeleton customer-form__action-skeleton--secondary" />
                  <SkeletonBlock className="customer-form__action-skeleton" />
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
                    {submitting ? 'Saving…' : submitLabel}
                  </Button>
                </>
              )}
            </div>
          )}
        </form>
      </SkeletonProvider>

      {/* Attribute value deletion confirmation dialog */}
      <ConfirmDeleteDialog
        open={Boolean(deletingAttributeValue)}
        onOpenChange={(open) => {
          if (!open) setDeletingAttributeValue(null);
        }}
        title="Delete attribute value"
        confirmKeyword={deletingAttributeValue?.currentValue ?? ''}
        description={
          <>
            This will permanently remove the stored value for{' '}
            <strong>{deletingAttributeValue?.name}</strong>.
          </>
        }
        isPending={deletingAttributeValuePending}
        onConfirm={handleDeleteAttributeValue}
      />
    </>
  );
}
